/**
 * Pinch-to-zoom + pan pentru galerii imagini (meniu, promo editabil).
 */

const MAX_SCALE = 3.5;
const MIN_SCALE = 1;
const DOUBLE_TAP_MS = 320;
const DOUBLE_TAP_SCALE = 2.25;
const SWIPE_THRESHOLD = 48;
const TAP_SLOP = 12;
const PINCH_COOLDOWN_MS = 450;

/**
 * Înveliș DOM: viewport > stage > img
 */
export function wrapImageWithZoom(img) {
  img.classList.add('gallery-zoom-img');

  const viewport = document.createElement('div');
  viewport.className = 'gallery-zoom-viewport';

  const stage = document.createElement('div');
  stage.className = 'gallery-zoom-stage';
  stage.appendChild(img);
  viewport.appendChild(stage);

  return { viewport, stage, img };
}

/**
 * @returns {{ reset: Function, isZoomed: Function, destroy: Function, getScale: Function }}
 */
export function attachImageGalleryZoom(viewport, stage, callbacks = {}) {
  const pointers = new Map();
  let scale = 1;
  let tx = 0;
  let ty = 0;
  let pinchStartDist = 0;
  let pinchStartScale = 1;
  let panStartX = 0;
  let panStartY = 0;
  let panOriginTx = 0;
  let panOriginTy = 0;
  let lastTapTime = 0;
  let pinching = false;
  let gestureHadTwoPointers = false;
  let pinchCooldownUntil = 0;
  let gestureMaxMove = 0;
  let gestureStartX = 0;
  let gestureStartY = 0;

  function syncZoomClass() {
    viewport.classList.toggle('gallery-zoom-viewport--zoomed', scale > 1.02);
  }

  function notifyZoom() {
    syncZoomClass();
    callbacks.onZoomChange?.(scale > 1.02);
  }

  function applyTransform(animate) {
    stage.style.transition = animate ? 'transform 0.22s ease' : 'none';
    stage.style.transform = `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`;
  }

  function clampPan() {
    if (scale <= 1) {
      tx = 0;
      ty = 0;
      return;
    }
    const vw = viewport.clientWidth || 1;
    const vh = viewport.clientHeight || 1;
    const maxX = ((scale - 1) * vw) / 2 + vw * 0.2;
    const maxY = ((scale - 1) * vh) / 2 + vh * 0.2;
    tx = Math.max(-maxX, Math.min(maxX, tx));
    ty = Math.max(-maxY, Math.min(maxY, ty));
  }

  function reset() {
    scale = 1;
    tx = 0;
    ty = 0;
    pinching = false;
    gestureHadTwoPointers = false;
    gestureMaxMove = 0;
    pointers.clear();
    applyTransform(true);
    notifyZoom();
  }

  function isZoomed() {
    return scale > 1.02;
  }

  function getScale() {
    return scale;
  }

  function pointerDistance(a, b) {
    const dx = a.clientX - b.clientX;
    const dy = a.clientY - b.clientY;
    return Math.hypot(dx, dy);
  }

  function trackGestureMove(x, y) {
    gestureMaxMove = Math.max(
      gestureMaxMove,
      Math.hypot(x - gestureStartX, y - gestureStartY)
    );
  }

  function onPointerDown(e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    if (pointers.size === 0) {
      gestureMaxMove = 0;
      gestureStartX = e.clientX;
      gestureStartY = e.clientY;
    }

    pointers.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });

    if (pointers.size === 2) {
      gestureHadTwoPointers = true;
      pinching = true;
      viewport.setPointerCapture?.(e.pointerId);
      const [a, b] = Array.from(pointers.values());
      pinchStartDist = pointerDistance(a, b);
      pinchStartScale = scale;
      stage.style.transition = 'none';
    } else if (pointers.size === 1 && isZoomed()) {
      viewport.setPointerCapture?.(e.pointerId);
      panStartX = e.clientX;
      panStartY = e.clientY;
      panOriginTx = tx;
      panOriginTy = ty;
      stage.style.transition = 'none';
    }
  }

  function onPointerMove(e) {
    if (!pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });
    trackGestureMove(e.clientX, e.clientY);

    if (pointers.size >= 2 && pinching) {
      const [a, b] = Array.from(pointers.values());
      const dist = pointerDistance(a, b);
      if (pinchStartDist > 0) {
        scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, (pinchStartScale * dist) / pinchStartDist));
        clampPan();
        applyTransform(false);
        notifyZoom();
      }
      return;
    }

    if (pointers.size === 1 && isZoomed() && !pinching) {
      tx = panOriginTx + (e.clientX - panStartX);
      ty = panOriginTy + (e.clientY - panStartY);
      clampPan();
      applyTransform(false);
    }
  }

  function finishGesture() {
    const wasPinch = gestureHadTwoPointers;

    if (wasPinch) {
      pinchCooldownUntil = Date.now() + PINCH_COOLDOWN_MS;
    }

    if (scale < 1.01) {
      scale = 1;
      tx = 0;
      ty = 0;
    } else {
      clampPan();
    }
    applyTransform(true);
    notifyZoom();

    const isTap = gestureMaxMove < TAP_SLOP;
    const now = Date.now();
    const pinchCooldownActive = now < pinchCooldownUntil;

    if (isTap && !wasPinch && !pinchCooldownActive && pointers.size === 0) {
      if (now - lastTapTime < DOUBLE_TAP_MS) {
        if (isZoomed()) {
          reset();
        } else {
          scale = DOUBLE_TAP_SCALE;
          clampPan();
          applyTransform(true);
          notifyZoom();
        }
        lastTapTime = 0;
      } else {
        lastTapTime = now;
      }
    } else if (wasPinch || !isTap) {
      lastTapTime = 0;
    }

    gestureHadTwoPointers = false;
    gestureMaxMove = 0;
    pinching = false;
    pinchStartDist = 0;
  }

  function onPointerUp(e) {
    if (!pointers.has(e.pointerId)) return;
    trackGestureMove(e.clientX, e.clientY);
    pointers.delete(e.pointerId);
    try {
      viewport.releasePointerCapture?.(e.pointerId);
    } catch (_) {}

    if (pointers.size === 0) {
      finishGesture();
    } else if (pointers.size === 1 && pinching) {
      pinching = false;
      pinchStartDist = 0;
    }
  }

  function onPointerCancel(e) {
    if (!pointers.has(e.pointerId)) return;
    pointers.delete(e.pointerId);
    try {
      viewport.releasePointerCapture?.(e.pointerId);
    } catch (_) {}
    if (pointers.size === 0) {
      finishGesture();
    }
  }

  viewport.addEventListener('pointerdown', onPointerDown);
  viewport.addEventListener('pointermove', onPointerMove);
  viewport.addEventListener('pointerup', onPointerUp);
  viewport.addEventListener('pointercancel', onPointerCancel);

  syncZoomClass();

  function destroy() {
    viewport.removeEventListener('pointerdown', onPointerDown);
    viewport.removeEventListener('pointermove', onPointerMove);
    viewport.removeEventListener('pointerup', onPointerUp);
    viewport.removeEventListener('pointercancel', onPointerCancel);
    reset();
  }

  return { reset, isZoomed, destroy, getScale };
}

/**
 * Swipe orizontal între slide-uri; blocat când zoom activ pe slide-ul curent.
 */
export function attachGallerySwipe(viewport, options) {
  const { canSwipe, onSwipePrev, onSwipeNext } = options;
  let startX = 0;
  let startY = 0;
  let active = false;
  let horizontalIntent = false;

  function onDown(e) {
    if (typeof canSwipe === 'function' && !canSwipe()) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    active = true;
    horizontalIntent = false;
    startX = e.clientX ?? 0;
    startY = e.clientY ?? 0;
  }

  function onMove(e) {
    if (!active) return;
    if (typeof canSwipe === 'function' && !canSwipe()) {
      active = false;
      return;
    }
    const dx = (e.clientX ?? startX) - startX;
    const dy = (e.clientY ?? startY) - startY;
    if (!horizontalIntent && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
      horizontalIntent = Math.abs(dx) >= Math.abs(dy);
      if (!horizontalIntent) {
        active = false;
      }
    }
  }

  function onUp(e) {
    if (!active) return;
    active = false;
    if (typeof canSwipe === 'function' && !canSwipe()) return;
    if (!horizontalIntent) return;
    const x = e.clientX ?? e.changedTouches?.[0]?.clientX ?? startX;
    const dx = x - startX;
    if (dx < -SWIPE_THRESHOLD) onSwipeNext?.();
    else if (dx > SWIPE_THRESHOLD) onSwipePrev?.();
  }

  viewport.addEventListener('pointerdown', onDown, { passive: true });
  viewport.addEventListener('pointermove', onMove, { passive: true });
  viewport.addEventListener('pointerup', onUp);
  viewport.addEventListener('pointercancel', onUp);

  return () => {
    viewport.removeEventListener('pointerdown', onDown);
    viewport.removeEventListener('pointermove', onMove);
    viewport.removeEventListener('pointerup', onUp);
    viewport.removeEventListener('pointercancel', onUp);
  };
}

/**
 * Slide galerie cu zoom integrat.
 */
export function buildGalleryZoomSlide(url, options = {}) {
  const { slideClass = '', imgClass = '', onLoad, onError } = options;

  const slide = document.createElement('div');
  slide.className = slideClass;

  const img = document.createElement('img');
  if (imgClass) img.className = imgClass;
  img.alt = '';
  img.decoding = 'async';
  img.loading = 'lazy';
  img.onload = () => {
    img.classList.add('img-loaded');
    onLoad?.(img);
  };
  img.onerror = () => {
    img.classList.add('img-failed');
    onError?.(img);
  };
  img.src = url;

  const { viewport, stage } = wrapImageWithZoom(img);
  slide.appendChild(viewport);

  const zoom = attachImageGalleryZoom(viewport, stage);

  return { slide, img, viewport, stage, zoom };
}
